import { useEffect, useState } from 'react'
import { Check, X, Ban, RotateCcw, Trash2, LineChart, ShieldAlert, ShieldOff, Coins, Mail } from 'lucide-react'
import { chartAdminService } from '@/services/chartAdminService'

function LevelBadge({ level }) {
  const s = level === 2
    ? 'bg-violet-500/20 text-violet-500 dark:text-violet-400'
    : 'bg-cyan-500/20 text-cyan-600 dark:text-cyan-400'
  return <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${s}`}>nivel {level}</span>
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-800 rounded-xl w-full max-w-md p-6 space-y-4 mx-4">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-slate-900 dark:text-white">{title}</h3>
          <button onClick={onClose} className="text-slate-500 dark:text-gray-400 hover:text-slate-900 dark:hover:text-white"><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  )
}

/* Confirmación explícita antes de conceder nivel 2 -- este usuario deja de
 * ser un simple viewer del chart y pasa a poder operar en real. */
function GrantAgentsModal({ user, onClose, onConfirm, loading }) {
  return (
    <Modal title="Conceder nivel 2 (agentes)" onClose={onClose}>
      <div className="flex items-start gap-3 bg-amber-500/10 border border-amber-500/30 text-amber-700 dark:text-amber-300 rounded-lg px-3 py-2.5 text-sm">
        <ShieldAlert size={18} className="shrink-0 mt-0.5" />
        <p>
          <strong>{user.username}</strong> podrá operar en real: crear bots, conectar cuentas de exchange y
          activar agentes. Esta acción es reversible, pero tenlo claro antes de confirmar.
        </p>
      </div>
      <div className="flex gap-2 pt-1">
        <button onClick={onConfirm} disabled={loading} className="btn-primary">
          {loading ? 'Concediendo…' : `Sí, ${user.username} podrá operar en real`}
        </button>
        <button onClick={onClose} className="btn-ghost">Cancelar</button>
      </div>
    </Modal>
  )
}

/* Confirmación explícita antes de conceder Rango Premium -- real-money,
 * invite-only. Requiere nivel 2 ya concedido (invariante de DB). */
function GrantRangoPremiumModal({ user, onClose, onConfirm, loading }) {
  return (
    <Modal title="Conceder Rango Premium" onClose={onClose}>
      <div className="flex items-start gap-3 bg-amber-500/10 border border-amber-500/30 text-amber-700 dark:text-amber-300 rounded-lg px-3 py-2.5 text-sm">
        <Coins size={18} className="shrink-0 mt-0.5" />
        <p>
          <strong>{user.username}</strong> podrá activar el agente Rango Premium sobre su propia cuenta
          Bitunix -- estrategia real, dinero real. Esta acción es reversible, pero tenlo claro antes de confirmar.
        </p>
      </div>
      <div className="flex gap-2 pt-1">
        <button onClick={onConfirm} disabled={loading} className="btn-primary">
          {loading ? 'Concediendo…' : `Sí, ${user.username} podrá activar Rango Premium`}
        </button>
        <button onClick={onClose} className="btn-ghost">Cancelar</button>
      </div>
    </Modal>
  )
}

/* Pestaña de gestión de niveles de acceso (/admin/users/*) -- una misma
 * fila `users` puede tener nivel 1 (has_chart_access, ve el chart y el
 * análisis diario) y/o nivel 2 (has_agents_access, opera bots/exchange en
 * real). Reemplaza el modelo chart_users de un solo `status`, retirado en
 * la migración de niveles de acceso (docs/auth/diseno-niveles-acceso.md). */
export default function ChartAccessTab() {
  const [requests, setRequests] = useState([])
  const [users, setUsers]       = useState([])
  const [loading, setLoading]   = useState(true)
  const [error, setError]       = useState(null)
  const [busyId, setBusyId]     = useState(null)
  const [grantModalUser, setGrantModalUser] = useState(null)
  const [grantRangoPremiumUser, setGrantRangoPremiumUser] = useState(null)

  const load = () => {
    setLoading(true)
    Promise.all([chartAdminService.listRequests(), chartAdminService.listUsers()])
      .then(([reqRes, usersRes]) => {
        setRequests(reqRes.data)
        setUsers(usersRes.data)
        setError(null)
      })
      .catch((err) => setError(err.response?.data?.detail || 'Error al cargar el acceso al chart'))
      .finally(() => setLoading(false))
  }

  useEffect(load, [])

  const runAction = async (id, action) => {
    setBusyId(id)
    try {
      await action()
      load()
    } catch (err) {
      alert(err.response?.data?.detail || 'Error')
    } finally {
      setBusyId(null)
    }
  }

  const handleApprove = (r) => runAction(r.id, () => chartAdminService.approve(r.id))
  const handleDeny    = (r) => {
    const who = r.requested_username || r.email || 'esta solicitud'
    if (!confirm(`¿Denegar la solicitud de "${who}"?`)) return
    runAction(r.id, () => chartAdminService.deny(r.id))
  }

  const handleResendSetPassword = (u) => {
    if (!confirm(`¿Reenviar el enlace de fijar contraseña a "${u.username}"? Invalida el enlace anterior si seguía sin usarse.`)) return
    runAction(u.id, () => chartAdminService.resendSetPassword(u.id))
  }

  const handleConfirmGrant = () => {
    const user = grantModalUser
    setGrantModalUser(null)
    runAction(user.id, () => chartAdminService.grantAgents(user.id))
  }
  const handleRevokeAgents = (u) => {
    const extra = u.has_rango_premium_access ? ' Al no tener ya nivel 2, también pierde Rango Premium con esto.' : ''
    if (!confirm(`¿Revocar el nivel 2 (agentes) de "${u.username}"? Dejará de poder operar en real de inmediato.${extra}`)) return
    runAction(u.id, () => chartAdminService.revokeAgents(u.id))
  }
  const handleConfirmGrantRangoPremium = () => {
    const user = grantRangoPremiumUser
    setGrantRangoPremiumUser(null)
    runAction(user.id, () => chartAdminService.grantRangoPremium(user.id))
  }
  const handleRevokeRangoPremium = (u) => {
    if (!confirm(`¿Revocar Rango Premium de "${u.username}"? No podrá activar el agente de nuevo hasta que se le vuelva a conceder (los agentes ya activos siguen operando hasta que él mismo los pause).`)) return
    runAction(u.id, () => chartAdminService.revokeRangoPremium(u.id))
  }
  const handleRevokeChart = (u) => {
    const extra = u.has_agents_access ? ' Al no tener ya nivel 1, también pierde el nivel 2 (agentes) con esto.' : ''
    if (!confirm(`¿Revocar el acceso al chart de "${u.username}"?${extra}`)) return
    runAction(u.id, () => chartAdminService.revokeChart(u.id))
  }
  const handleDisable = (u) => {
    if (!confirm(`¿Deshabilitar la cuenta de "${u.username}"? Se corta de inmediato.`)) return
    runAction(u.id, () => chartAdminService.disable(u.id))
  }
  const handleEnable = (u) => runAction(u.id, () => chartAdminService.enable(u.id))
  const handleDelete = (u) => {
    if (!confirm(`¿Eliminar definitivamente la cuenta "${u.username}"? No se puede deshacer.`)) return
    runAction(u.id, () => chartAdminService.remove(u.id))
  }

  if (loading) return <p className="text-slate-500 dark:text-gray-400 text-sm">Cargando…</p>

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 bg-cyan-500/10 border border-cyan-500/20 text-cyan-700 dark:text-cyan-300 rounded-lg px-3 py-2 text-xs">
        <LineChart size={14} className="shrink-0" />
        Niveles de acceso al <strong>chart público</strong> (getquantum.app/chart) — nivel 1 ve el chart y el
        análisis diario, nivel 2 además opera bots/exchange en real. Cuentas separadas de los usuarios de la
        plataforma, sin rol de la app privada.
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 text-red-700 dark:text-red-300 rounded-lg px-4 py-3 text-sm">
          {error}
        </div>
      )}

      <section className="space-y-2">
        <h2 className="text-sm font-semibold text-slate-700 dark:text-gray-300">
          Solicitudes pendientes {requests.length > 0 && `(${requests.length})`}
        </h2>
        {requests.length === 0 ? (
          <p className="text-sm text-slate-400 dark:text-gray-500">No hay solicitudes pendientes.</p>
        ) : (
          <div className="space-y-2">
            {requests.map((r) => (
              <div key={r.id} className="card flex items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-slate-900 dark:text-gray-100">
                      {r.requested_username || '(usuario existente)'}
                    </span>
                    <LevelBadge level={r.level} />
                  </div>
                  {r.email && <p className="text-sm text-slate-500 dark:text-gray-400 truncate">{r.email}</p>}
                  <p className="text-xs text-slate-400 dark:text-gray-500">
                    solicitada {new Date(r.created_at).toLocaleString()}
                  </p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={() => handleApprove(r)}
                    disabled={busyId === r.id}
                    title="Aprobar"
                    className="p-1.5 text-emerald-500 hover:text-emerald-400 rounded"
                  >
                    <Check size={16} />
                  </button>
                  <button
                    onClick={() => handleDeny(r)}
                    disabled={busyId === r.id}
                    title="Denegar"
                    className="p-1.5 text-red-500 hover:text-red-400 rounded"
                  >
                    <X size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="space-y-2">
        <h2 className="text-sm font-semibold text-slate-700 dark:text-gray-300">Cuentas</h2>
        {users.length === 0 ? (
          <p className="text-sm text-slate-400 dark:text-gray-500">No hay cuentas todavía.</p>
        ) : (
          <div className="space-y-2">
            {users.map((u) => (
              <div key={u.id} className="card flex items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-slate-900 dark:text-gray-100">{u.username}</span>
                    {u.has_chart_access && <LevelBadge level={1} />}
                    {u.has_agents_access && <LevelBadge level={2} />}
                    {!u.has_chart_access && !u.password_changed_at && (
                      <span className="text-xs px-1.5 py-0.5 rounded font-medium bg-slate-500/20 text-slate-600 dark:text-gray-400">
                        pendiente de fijar contraseña
                      </span>
                    )}
                    {u.has_rango_premium_access && (
                      <span className="text-xs px-1.5 py-0.5 rounded font-medium bg-amber-500/20 text-amber-600 dark:text-amber-400">
                        Nivel 3 · Rango Premium
                      </span>
                    )}
                    {u.totp_enabled && (
                      <span className="text-xs px-1.5 py-0.5 rounded bg-green-500/20 text-green-500 dark:text-green-400">2FA</span>
                    )}
                    {!u.is_active && (
                      <span className="text-xs px-1.5 py-0.5 rounded bg-red-500/20 text-red-500 dark:text-red-400">deshabilitada</span>
                    )}
                  </div>
                  <p className="text-sm text-slate-500 dark:text-gray-400 truncate">{u.email}</p>
                  <p className="text-xs text-slate-400 dark:text-gray-500">
                    último acceso: {u.last_login ? new Date(u.last_login).toLocaleString() : 'nunca'}
                  </p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {u.is_active && !u.has_chart_access && !u.password_changed_at && (
                    <button
                      onClick={() => handleResendSetPassword(u)}
                      disabled={busyId === u.id}
                      title="Reenviar enlace de fijar contraseña"
                      className="p-1.5 text-cyan-500 hover:text-cyan-400 rounded"
                    >
                      <Mail size={15} />
                    </button>
                  )}
                  {u.is_active && !u.has_agents_access && (
                    <button
                      onClick={() => setGrantModalUser(u)}
                      disabled={busyId === u.id}
                      title="Conceder nivel 2 (agentes)"
                      className="p-1.5 text-violet-500 hover:text-violet-400 rounded"
                    >
                      <ShieldAlert size={15} />
                    </button>
                  )}
                  {u.is_active && u.has_agents_access && (
                    <button
                      onClick={() => handleRevokeAgents(u)}
                      disabled={busyId === u.id}
                      title="Revocar nivel 2 (agentes)"
                      className="p-1.5 text-violet-500 hover:text-red-400 rounded"
                    >
                      <ShieldOff size={15} />
                    </button>
                  )}
                  {u.is_active && u.has_agents_access && !u.has_rango_premium_access && (
                    <button
                      onClick={() => setGrantRangoPremiumUser(u)}
                      disabled={busyId === u.id}
                      title="Conceder Rango Premium"
                      className="p-1.5 text-amber-500 hover:text-amber-400 rounded"
                    >
                      <Coins size={15} />
                    </button>
                  )}
                  {u.is_active && u.has_rango_premium_access && (
                    <button
                      onClick={() => handleRevokeRangoPremium(u)}
                      disabled={busyId === u.id}
                      title="Revocar Rango Premium"
                      className="p-1.5 text-amber-500 hover:text-red-400 rounded"
                    >
                      <Coins size={15} />
                    </button>
                  )}
                  {u.is_active && (
                    <button
                      onClick={() => handleRevokeChart(u)}
                      disabled={busyId === u.id}
                      title="Revocar nivel 1 (chart)"
                      className="p-1.5 text-cyan-500 hover:text-red-400 rounded"
                    >
                      <LineChart size={15} />
                    </button>
                  )}
                  {u.is_active ? (
                    <button
                      onClick={() => handleDisable(u)}
                      disabled={busyId === u.id}
                      title="Deshabilitar"
                      className="p-1.5 text-slate-500 dark:text-gray-400 hover:text-red-400 rounded"
                    >
                      <Ban size={15} />
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={() => handleEnable(u)}
                        disabled={busyId === u.id}
                        title="Reactivar"
                        className="p-1.5 text-emerald-500 hover:text-emerald-400 rounded"
                      >
                        <RotateCcw size={15} />
                      </button>
                      <button
                        onClick={() => handleDelete(u)}
                        disabled={busyId === u.id}
                        title="Eliminar"
                        className="p-1.5 text-slate-500 dark:text-gray-400 hover:text-red-400 rounded"
                      >
                        <Trash2 size={15} />
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {grantModalUser && (
        <GrantAgentsModal
          user={grantModalUser}
          onClose={() => setGrantModalUser(null)}
          onConfirm={handleConfirmGrant}
          loading={busyId === grantModalUser.id}
        />
      )}
      {grantRangoPremiumUser && (
        <GrantRangoPremiumModal
          user={grantRangoPremiumUser}
          onClose={() => setGrantRangoPremiumUser(null)}
          onConfirm={handleConfirmGrantRangoPremium}
          loading={busyId === grantRangoPremiumUser.id}
        />
      )}
    </div>
  )
}
